import { Calendar, ChevronRight, Layers, Ticket, type LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { cn } from "@/lib/utils/general";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useMediaQuery } from "@/lib/hooks/use-media-query";

interface CreateOption {
  icon: LucideIcon;
  title: string;
  description: string;
  path: string;
}

const OPTIONS: CreateOption[] = [
  {
    icon: Calendar,
    title: "Class",
    description: "A single pop-up class. Students book and pay per session.",
    path: "/classes/new",
  },
  {
    icon: Layers,
    title: "Series",
    description:
      "Multiple pop-up classes bundled together, with tiered pricing by class count.",
    path: "/series/new",
  },
  {
    icon: Ticket,
    title: "Event",
    description:
      "A one-time ticketed occasion — workshops, showcases, parties.",
    path: "/events/new",
  },
  // TODO: Unhide when Programs feature is ready for launch
  // {
  //   icon: ClipboardList,
  //   title: "Program",
  //   description:
  //     "A multi-session program with optional applications and recurring payments.",
  //   path: "/programs/new",
  // },
];

interface CreatePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreatePicker({ open, onOpenChange }: CreatePickerProps) {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const handleSelect = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  const title = "What do you want to create?";
  const description = "Pick the type of listing that fits your offering.";

  const content = (
    <div className="overflow-hidden rounded-xl bg-white/5">
      {OPTIONS.map(({ icon: Icon, title: optTitle, description: optDesc, path }, idx) => (
        <button
          key={path}
          type="button"
          onClick={() => handleSelect(path)}
          className={cn(
            "flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-white/5 focus:outline-none focus-visible:bg-white/5",
            idx > 0 && "border-t border-white/10"
          )}
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10">
            <Icon className="h-4 w-4 text-on-dark" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-sm font-medium text-on-dark leading-tight">
              {optTitle}
            </span>
            <span className="text-xs text-on-dark-muted leading-snug">
              {optDesc}
            </span>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-on-dark-muted" />
        </button>
      ))}
    </div>
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className="mt-2">{content}</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl bg-card">
        <SheetHeader className="text-left">
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>
        <div className="mt-6 pb-4">{content}</div>
      </SheetContent>
    </Sheet>
  );
}
